import axios from 'axios';
import config from '../config';
import { useTranslation } from "react-i18next";
import React, { useMemo, useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { Link, useNavigate, useLocation } from 'react-router-dom';
import NavGroups from './NavGroups';
import BrandMark from '../components/BrandMark';

import i18n from "../i18n";
const tr = i18n.t.bind(i18n);



// Οι σελίδες των δύο «αυτοτελών» ενοτήτων — εμφανίζονται ως καρτέλες στο
// navbar όταν ο χρήστης βρίσκεται μέσα τους (βλ. navGroups παρακάτω).
const BUZZ_PAGES = [
  { to: '/buzz/board',    label: 'Board',        icon: '🔔' },
  { to: '/buzz/zones',    label: 'Zones',        icon: '🗺' },
  { to: '/buzz/tables',   label: 'Tables',       icon: '🍽' },
  { to: '/buzz/reports',  label: 'Reports',      icon: '📊' },
  { to: '/buzz/team',     label: 'Team',         icon: '👥' },
  { to: '/buzz/settings', label: 'Settings',     icon: '⚙️' },
  { to: '/buzz/billing',  label: 'Subscription', icon: '💳' },
];
const ADMIN_PAGES = [
  { to: '/admin/overview',  label: 'Overview',           icon: '📋' },
  { to: '/admin/companies', label: 'Customers',          icon: '🏪' },
  { to: '/admin/products',  label: 'Products & services', icon: '📦' },
  { to: '/admin/payments',  label: 'Payments',           icon: '💳' },
  { to: '/admin/leads',     label: 'Leads',              icon: '✨' },
  { to: '/admin/invoices',  label: 'Invoices',           icon: '🧾' },
  { to: '/admin/system',    label: 'System status',      icon: '🩺' },
];

const Navbar = ({ user, onLogout, planPerms, onLanguageChange  }) => {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 600);
  const [isMid, setIsMid]       = useState(() => window.innerWidth >= 600 && window.innerWidth < 1500);
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation(); // Get the current path to highlight the selected tab

  const inPlan = (perm) => {
    if (!planPerms) return true;   // before load: don't hide yet
    return !!planPerms[perm];
  };

  // Station pages appear ONLY when the menu setup wires a category to them.
  const [stationsInUse, setStationsInUse] = useState([]);
  useEffect(() => {
    if (!user) { setStationsInUse([]); return; }
    let alive = true;
    axios.get(`${config.API_BASE_URL}/ordering/stations-in-use`)
      .then(({ data }) => { if (alive) setStationsInUse(Array.isArray(data?.stations) ? data.stations : []); })
      .catch(() => { if (alive) setStationsInUse([]); });
    return () => { alive = false; };
  }, [user]);
  const stationWired = (st) => stationsInUse.includes(st);

  // Δυναμικά πόστα εταιρείας (8/8/2026): τα custom (μη-legacy) πόστα μπαίνουν
  // στο μενού Stations με το όνομά τους, σε route /station/<skey>.
  const LEGACY_KEYS = ['bbq', 'kitchen', 'bar', 'salads', 'desserts'];
  const [customStations, setCustomStations] = useState([]);
  useEffect(() => {
    if (!user) { setCustomStations([]); return; }
    let alive = true;
    axios.get(`${config.API_BASE_URL}/stations-config`)
      .then(({ data }) => {
        if (!alive) return;
        setCustomStations((Array.isArray(data) ? data : [])
          .filter((s) => s.active && !LEGACY_KEYS.includes(s.skey)));
      })
      .catch(() => { if (alive) setCustomStations([]); });
    return () => { alive = false; };
  }, [user]);

  // Shift-based station restriction: when scheduled today on specific stations,
  // a non-admin only sees those station pages.
  const [myStations, setMyStations] = useState(null);
  useEffect(() => {
    if (!user) { setMyStations(null); return; }
    let alive = true;
    axios.get(`${config.API_BASE_URL}/my-stations`)
      .then(({ data }) => { if (alive) setMyStations(Array.isArray(data?.stations) ? data.stations : null); })
      .catch(() => { if (alive) setMyStations(null); });
    return () => { alive = false; };
  }, [user]);
  const onShift = (st) => user?.view_admin || myStations === null || myStations.includes(st);

  const LANGS = [
    { code: "en", label: "English", flag: "/locales/en/flag.png" },
    { code: "el", label: "Ελληνικά", flag: "/locales/el/flag.png" },
  ];

  function LanguageSwitcher({ direction = "down", onChange }) {
    const { i18n } = useTranslation();
    const current = (i18n.resolvedLanguage || i18n.language || "en").split("-")[0];

    const currentLang = useMemo(
      () => LANGS.find((l) => l.code === current) || LANGS[0],
      [current]
    );

    const [open, setOpen] = useState(false);
    const buttonRef = useRef(null);
    const menuRef = useRef(null);
    const [menuStyle, setMenuStyle] = useState({ top: 0, left: 0, minWidth: 120 });

    const preferUp = direction === "up" || isMid || isMobile;

    useEffect(() => {
      if (!open) return;

      const updateMenuPosition = () => {
        const btn = buttonRef.current;
        if (!btn) return;

        const rect = btn.getBoundingClientRect();
        const gap = 6;
        const estimatedMenuHeight = 96;

        let top = preferUp
          ? Math.max(8, rect.top - estimatedMenuHeight - gap)
          : Math.min(window.innerHeight - 8, rect.bottom + gap);

        const left = Math.max(8, rect.right - Math.max(rect.width, 120));

        setMenuStyle({
          top,
          left,
          minWidth: Math.max(rect.width, 120),
        });
      };

      const onDocMouseDown = (e) => {
        const target = e.target;
        if (buttonRef.current?.contains(target)) return;
        if (menuRef.current?.contains(target)) return;
        setOpen(false);
      };

      updateMenuPosition();
      document.addEventListener("mousedown", onDocMouseDown);
      window.addEventListener("resize", updateMenuPosition);
      window.addEventListener("scroll", updateMenuPosition, true);

      return () => {
        document.removeEventListener("mousedown", onDocMouseDown);
        window.removeEventListener("resize", updateMenuPosition);
        window.removeEventListener("scroll", updateMenuPosition, true);
      };
    }, [open, preferUp]);

    const setLang = async (lng) => {
      setOpen(false);
      if (onChange) return onChange(lng);
      return i18n.changeLanguage(lng);
    };

    return (
      <>
        <div style={{ position: "relative", display: "inline-block" }}>
          <button
            ref={buttonRef}
            type="button"
            onClick={() => setOpen((v) => !v)}
            aria-haspopup="menu"
            aria-expanded={open}
            title={`Language: ${currentLang.label}`}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              padding: "5px",
              border: "1px solid #ccc",
              borderRadius: 5,
              background: "#fff",
              color: "#111",
              cursor: "pointer",
            }}
          >
            <img
              src={currentLang.flag}
              alt={`${currentLang.label}`}
              width={20}
              height={14}
              style={{ display: "block", borderRadius: 2, objectFit: "cover" }}
            />
          </button>
        </div>

        {open && typeof document !== "undefined" &&
          createPortal(
            <div
              ref={menuRef}
              role="menu"
              style={{
                position: "fixed",
                left: menuStyle.left,
                top: menuStyle.top,
                border: "1px solid #ddd",
                borderRadius: 8,
                background: "#fff",
                boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
                overflow: "hidden",
                minWidth: menuStyle.minWidth,
                zIndex: 10000,
              }}
            >
              {LANGS.map((l) => (
                <button
                  key={l.code}
                  role="menuitemradio"
                  aria-checked={l.code === current}
                  onClick={() => {
                    setLang(l.code);
                  }}
                  style={{
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    padding: "8px 10px",
                    border: 0,
                    background: l.code === current ? "#f5f5f5" : "#fff",
                    cursor: "pointer",
                    textAlign: "left",
                  }}
                >
                  <img
                    src={l.flag}
                    alt={`${l.label}`}
                    width={20}
                    height={14}
                    style={{ display: "block", borderRadius: 2, objectFit: "cover" }}
                  />
                  <span style={{ fontSize: 13 }}>{l.label}</span>
                </button>
              ))}
            </div>,
            document.body
          )}
      </>
    );
  }

  const canSee = (perm) => {
    if (!user) return false;
    const hasUser = user.view_admin || user[perm];
    return hasUser && inPlan(perm);
  };

  const canSeeBilling = () => !!(user && (user.view_admin || user.view_billing));

  // Buzz: εκτός plan gating — πραγματικός έλεγχος του BIT (τα flags έρχονται ως Buffer objects)
  const canSeeBuzz = () => {
    const v = user?.view_buzz;
    return v === 1 || v === true || v === '1' ||
      (!!v && typeof v === 'object' && Array.isArray(v.data) && v.data[0] === 1);
  };

  // /admin (super-admin): εκτός plan gating, ίδιος bit-safe έλεγχος με το Buzz.
  // Η πραγματική εξουσιοδότηση είναι server-side (flag + IP allowlist).
  const canSeeSuperAdmin = () => {
    const v = user?.view_superadmin;
    return v === 1 || v === true || v === '1' ||
      (!!v && typeof v === 'object' && Array.isArray(v.data) && v.data[0] === 1);
  };

  useEffect(() => {
    const checkMobile = () => {
      const w = window.innerWidth;
      setIsMobile(w < 600);
      setIsMid(w >= 600 && w < 1500);
    };
  
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Email ως username → δείχνουμε μόνο το τοπικό μέρος (πλήρες on hover)·
  // σκέτο όνομα → εμφανίζεται ολόκληρο.
  const shortUsername = (s) => {
    const v = String(s || 'User');
    return v.includes('@') ? v.split('@')[0] : v;
  };

  const handleLogout = async () => {
    await onLogout(); // App.js will redirect
  };


  // ── Ενιαίο μοντέλο πλοήγησης ────────────────────────────────────────────────
  // Ένας ορισμός για desktop / tablet / κινητό, ώστε καμία σελίδα να μη γίνεται
  // απρόσιτη σε κάποια συσκευή (πριν, το μενού κινητού έκρυβε 11 σελίδες).
  const navGroups = [
    {
      label: 'Reservations', icon: '📋', alwaysGroup: true,
      items: [
        { to: '/reservations-list', label: 'Reservations list', visible: canSee('view_reservations_list') },
        { to: '/reservation-form', label: 'New Reservation', visible: canSee('view_reservation_form') },
        { to: '/clients', label: 'Clients', visible: canSee('view_clients') },
      ],
    },
    {
      label: 'Stations', icon: '🍽', alwaysGroup: true,
      items: [
        { to: '/bbq', label: 'BBQ', icon: '🔥', visible: canSee('view_bbq') && onShift('bbq') },
        { to: '/kitchen', label: 'Kitchen', icon: '👨‍🍳', visible: canSee('view_kitchen') && onShift('kitchen') },
        // Ordering-only πλάνα: χωρίς view_kitchen δεν υπάρχει η σελίδα /kitchen —
        // η ουρά κουζίνας σερβίρεται από το generic StationScreen στο /station/kitchen
        { to: '/station/kitchen', label: 'Kitchen', icon: '👨‍🍳',
          visible: !canSee('view_kitchen') && canSee('view_orders') && stationWired('kitchen') && onShift('kitchen') },
        { to: '/bar', label: 'Bar', icon: '🍸', visible: canSee('view_bar') && stationWired('bar') && onShift('bar') },
        { to: '/salads', label: 'Salads', icon: '🥗', visible: canSee('view_salads') && stationWired('salads') && onShift('salads') },
        ...customStations.map((s) => ({
          to: `/station/${s.skey}`, label: s.name, icon: '🍽',
          visible: canSee('view_orders') && stationWired(s.skey),
        })),
        { to: '/desserts', label: 'Desserts', icon: '🍰', visible: canSee('view_desserts') && stationWired('desserts') && onShift('desserts') },
      ],
    },
    {
      // Service & Orders κάτω από την ίδια επιλογή μενού
      label: 'Service', icon: '🛎',
      items: [
        { to: '/service', label: 'Service', icon: '🛎', visible: canSee('view_service') },
        { to: '/orders', label: 'Orders', icon: '🧾', visible: canSee('view_orders') },
      ],
    },
    // Buzz & /admin: όσο ο χρήστης βρίσκεται ΜΕΣΑ στην ενότητα, οι σελίδες της
    // ανεβαίνουν ως καρτέλες πάνω στη γραμμή του μενού (αντί για δεύτερη σειρά
    // tabs μέσα στη σελίδα). Εκτός ενότητας μένει μία είσοδος, για να μη
    // φουσκώνει το μενού των πλήρων πακέτων.
    ...(canSeeBuzz() && location.pathname.startsWith('/buzz')
      ? BUZZ_PAGES.map(i => ({ label: i.label, icon: i.icon, items: [{ ...i, visible: true }] }))
      : [{ label: 'Buzz', icon: '🔔', items: [{ to: '/buzz/board', label: 'Buzz', visible: canSeeBuzz() }] }]),
    ...(canSeeSuperAdmin() && location.pathname.startsWith('/admin')
      ? ADMIN_PAGES.map(i => ({ label: i.label, icon: i.icon, items: [{ ...i, visible: true }] }))
      : [{ label: 'Admin', icon: '🛡️', items: [{ to: '/admin/overview', label: 'Admin', visible: canSeeSuperAdmin() }] }]),
    {
      label: 'Schedule', icon: '🗓',
      items: [
        { to: '/schedule', label: 'My schedule', visible: canSee('view_schedule') },
        { to: '/scheduler', label: 'Scheduler', visible: canSee('view_scheduler') },
      ],
    },
    {
      // Οι περίοδοι των Reports ζουν στο μενού (όχι σε μπάρα μέσα στη σελίδα)
      label: 'Reports', icon: '📊', alwaysGroup: true,
      items: [
        { to: '/reports', label: 'Daily', icon: '📅', visible: canSee('view_reports') },
        { to: '/reports?g=week', label: 'Weekly', icon: '🗓', visible: canSee('view_reports') },
        { to: '/reports?g=month', label: 'Monthly', icon: '📈', visible: canSee('view_reports') },
        // Ordering-only πλάνα: το sales report έχει δική του σελίδα όταν δεν υπάρχουν τα core Reports
        { to: '/orders/reports', label: 'Sales', icon: '💶', visible: !canSee('view_reports') && canSee('view_orders') },
      ],
    },
    {
      label: 'Setup', icon: '⚙️', alwaysGroup: true,
      items: [
        // Σειρά κατ' απαίτηση ιδιοκτήτη (5/11/2026): Menu, Tables, Users, Settings, Billing
        { to: '/orders/menu', label: 'Menu', icon: '🍽', visible: canSee('view_orders') },
        { to: '/tables', label: 'Tables', icon: '🪑', visible: canSee('view_tables') },
        { to: '/users', label: 'Users', icon: '👥', visible: canSee('view_users') },
        { to: '/settings', label: 'Settings', icon: '🔧', visible: canSee('view_settings') },
        { to: '/billing', label: 'Billing', icon: '💳', visible: canSeeBilling() },
      ],
    },
  ];

  // Επίπεδη λίστα (για redirect μετά το login και για τον έλεγχο πρόσβασης)
  const navFlat = navGroups.flatMap(g => g.items.filter(i => i.visible));




  useEffect(() => {
    // Only act on the root path
    if (location.pathname !== '/') return;

    if (!user) {
      navigate('/login', { replace: true });
      return;
    }

    // Ίδιο σύνολο προορισμών σε κάθε συσκευή — το μενού είναι πλέον ενιαίο
    const sourceLinks = navFlat;

    const firstVisible = sourceLinks.find(l => l.visible);
    navigate(firstVisible ? firstVisible.to : (canSeeBilling() ? '/billing' : '/login'), { replace: true });
  }, [user, isMobile, isMid, location.pathname, navigate, planPerms]);

  return (
    <>
      {isMobile ? (
        <div className="mobile-navbar">
          <div className="mobile-navbar-header">
            <div className="mobile-navbar-left" style={{gap: 0}}>
              <BrandMark size={28} href="/home" />
              {user && <span className="mobile-user">@{user.username}</span>}
            </div>
            {menuOpen && (<LanguageSwitcher  onChange={onLanguageChange} />)}
            <button className="mobile-menu-button" onClick={() => setMenuOpen(!menuOpen)}>{tr('☰')}</button>
          </div>

          {menuOpen && (
            <div className="mobile-navbar-menu">
              {navGroups
                .map(g => ({ ...g, items: g.items.filter(i => i.visible) }))
                .filter(g => g.items.length > 0)
                .map((g, gi) => (
                  <div key={gi} className="mnav-sec">
                    <div className="mnav-sec-title">{g.icon} {tr(g.label)}</div>
                    {g.items.map((it, ii) => (
                      <Link key={ii} to={it.to} onClick={() => setMenuOpen(false)}
                            className={
                              (it.to === location.pathname + location.search ||
                               (it.to === location.pathname && !location.search))
                                ? 'mnav-active' : undefined
                            }>
                        {it.icon ? `${it.icon} ` : ''}{tr(it.label)}
                      </Link>
                    ))}
                  </div>
                ))}
              <button onClick={handleLogout}>{tr('Logout')}</button>
            </div>
          )}
        </div>
      ) : isMid ? (
        <div className="mobile-navbar" style={{padding: 0}}>
          <div className="mobile-navbar-header">
             <h4 style={{ margin: 0 }}>
               <BrandMark size={28} href="/home" />
             </h4>
            {/* Mid range: primary links spread across width; gear before logout */}
            <div className="mobile-navbar-right" style={{ display: 'flex', alignItems: 'center', width: '100%', gap: 12 }}>
              <div className="mid-links-spread" style={{ display: 'flex', flex: 1, minWidth: 0 }}>
                {/* Κάτω μπάρα: τα dropdowns ανοίγουν προς τα πάνω */}
                <NavGroups groups={navGroups} dropUp />
              </div>

              <button
                onClick={handleLogout}
                style={{ padding: '5px 10px', backgroundColor: '#dc3545', color: 'white', border: 'none', borderRadius: '4px' }}
              >{tr('Logout:')}{' '}<b title={user.username || ''}>{shortUsername(user.username)}</b>
              </button>
              <LanguageSwitcher onChange={onLanguageChange} />
            </div>

          </div>
        </div>
      ) : (
      
        <nav
          style={{
            position: 'fixed',
            top: '0',
            left: '0',
            width: '100%',
            // Πάνω από τα sticky toolbars των σελίδων (200) ώστε τα dropdowns
            // του μενού να μην κόβονται, αλλά κάτω από τα modals (1000+)
            zIndex: '600',
            padding: '10px',
            backgroundColor: '#f8f9fa',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            borderBottom: '1px solid #ccc'
          }}
        >
          
         <h3 style={{ margin: 0, flex: '0 0 auto', marginRight: 14 }}>
           <BrandMark size={30} href="/home" />
         </h3>
        
        {/* Navigation Links — ομαδοποιημένα με αυτόματο overflow σε «Περισσότερα» */}
        <NavGroups groups={navGroups} />

        {/* User Info and Logout */}
        <div style={{
          marginRight: "10px",
          marginLeft: 14,
          flex: "0 0 auto",
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          gap: 10,
        }}>
          {user ? (
            <>
              <button
                onClick={handleLogout}
                style={{
                  padding: '5px 10px',
                  backgroundColor: '#dc3545',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                }}
              >{tr('Logout:')}{' '}<b title={user.username || ''}>{shortUsername(user.username)}</b>
              </button>

              <LanguageSwitcher onChange={onLanguageChange} />
            </>
          ) : (
            <Link
              to="/login"
              style={{
                padding: '5px 10px',
                backgroundColor: '#007bff',
                color: 'white',
                textDecoration: 'none',
                borderRadius: '4px',
              }}
            >{tr('Login')}</Link>
          )}
        </div>

      </nav>
      )}
    </>
  );
};

export default Navbar;
